import { buildAttribution } from "./attribution.js";
import { canTrack, resolveConsent } from "./consent.js";
import { buildEnvelope } from "./envelope.js";
import {
  getOrCreateSession,
  getOrCreateVisitor,
  markSeen,
  type SessionState,
  type VisitorState,
} from "./ids.js";
import { createTransport, type Transport } from "./transport.js";
import type { Consent, KeyValueStore, PixelConfig, PixelEvent, PixelEventType } from "./types.js";

const DEFAULT_IDLE_MS = 30 * 60 * 1000;

export interface PageContext {
  /** Current page URL (window.location.href in the browser). */
  url: string;
  /** document.referrer. */
  referrer: string;
  /** Date.now() — injected so tests are deterministic. */
  nowMs: number;
}

/**
 * The tracking client. Holds config + storage, owns visitor/session resolution, and turns a
 * `track(event, properties)` call into a built-and-sent envelope. Framework-free and
 * browser-API-free (it takes a KeyValueStore + PageContext), so the whole emit path unit-tests
 * without a DOM. The browser entry (browser.ts) wires it to cookies + window.
 */
export class PixelClient {
  private readonly config: PixelConfig;
  private readonly store: KeyValueStore;
  private readonly transport: Transport;

  constructor(config: PixelConfig, store: KeyValueStore, transport?: Transport) {
    this.config = config;
    this.store = store;
    this.transport = transport ?? createTransport(config);
  }

  /** Resolve the current session without sending an event. Useful for page-level boot logic. */
  currentSession(ctx: Pick<PageContext, "nowMs">): SessionState {
    const idleMs = this.config.sessionIdleMs ?? DEFAULT_IDLE_MS;
    return getOrCreateSession(this.store, ctx.nowMs, idleMs);
  }

  /**
   * Resolve the visitor without sending an event. `returning` flips to true only after a prior
   * event ran markSeen, so read it BEFORE the first track() of the page if you need the
   * pre-visit value (session.ts does, to derive return_visit).
   */
  currentVisitor(): VisitorState {
    return getOrCreateVisitor(this.store);
  }

  /**
   * Build the envelope for an event without sending it. Exposed for tests and for callers that
   * want to inspect/batch. Advances session + visitor state as a side effect.
   */
  async build(
    event: PixelEventType,
    properties: Record<string, unknown>,
    ctx: PageContext,
    consentOverride?: Consent,
  ): Promise<PixelEvent> {
    const idleMs = this.config.sessionIdleMs ?? DEFAULT_IDLE_MS;
    const visitor = getOrCreateVisitor(this.store);
    const session = getOrCreateSession(this.store, ctx.nowMs, idleMs);
    const attribution = buildAttribution(
      this.store,
      ctx.url,
      ctx.referrer,
      session.isNew || event === "session_start",
    );
    markSeen(this.store);

    return buildEnvelope({
      config: this.config,
      visitorId: visitor.visitorId,
      sessionId: session.sessionId,
      event,
      properties,
      attribution,
      nowMs: ctx.nowMs,
      consent: resolveConsent(this.config.consent, consentOverride),
    });
  }

  /** Returns the persistent visitor id (creates one on first call). Safe to call any time. */
  visitorId(): string {
    return getOrCreateVisitor(this.store).visitorId;
  }

  /**
   * Build + send. Returns false (without a network call) when consent forbids tracking, or when
   * the POST fails. Never throws — storefront stability comes before telemetry.
   */
  async track(
    event: PixelEventType,
    properties: Record<string, unknown>,
    ctx: PageContext,
    consentOverride?: Consent,
  ): Promise<boolean> {
    const consent = resolveConsent(this.config.consent, consentOverride);
    if (!canTrack(consent)) return false;
    const envelope = await this.build(event, properties, ctx, consentOverride);
    return this.transport.send(envelope);
  }
}
